import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, ArrowRight, Rocket, X } from "lucide-react";

// "3 steps to get started" checklist for teacher/parent home. Ticks off as they
// add a student, enable lessons and assign a lesson.
export default function OnboardingChecklist({ status, onAddStudent, onDismiss }) {
  const navigate = useNavigate();
  const steps = [
    { key: "has_student", title: "Add your first student", desc: "Create a login they'll use to sign in.", cta: "Add student", onClick: onAddStudent },
    { key: "has_enabled_lesson", title: "Turn on lessons", desc: "Enable the subjects your students should see.", cta: "Open Library", onClick: () => navigate("/library") },
    { key: "has_assignment", title: "Assign a lesson", desc: "Send a lesson to a student to complete.", cta: "Go to Lessons", onClick: () => navigate("/lessons") },
  ];
  const done = steps.filter((s) => status[s.key]).length;
  const pct = Math.round((done / steps.length) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="mt-6 overflow-hidden rounded-3xl border border-[#E5DECF] bg-white shadow-sm"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-[#0D9488] to-[#7A3FF2] px-5 py-4 text-white">
        <div className="flex items-center gap-2.5">
          <Rocket size={20} />
          <div>
            <div className="font-heading text-lg font-extrabold leading-tight">Get started in 3 steps</div>
            <div className="font-body text-xs text-white/85" data-testid="onboarding-progress">{done} of {steps.length} done</div>
          </div>
        </div>
        <button onClick={onDismiss} data-testid="onboarding-dismiss" className="rounded-xl p-1.5 text-white/90 hover:bg-white/20" aria-label="Dismiss checklist">
          <X size={18} />
        </button>
      </div>

      <div className="h-1.5 w-full bg-[#E5DECF]">
        <motion.div className="h-full bg-[#10B981]" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
      </div>

      <div className="divide-y divide-[#F2ECE1]">
        {steps.map((s, i) => {
          const complete = !!status[s.key];
          return (
            <div key={s.key} className="flex items-center gap-3 px-5 py-4" data-testid={`onboarding-step-${i}`}>
              {complete
                ? <CheckCircle2 size={24} className="shrink-0 text-[#10B981]" data-testid={`onboarding-check-${i}`} />
                : <Circle size={24} className="shrink-0 text-[#CBD5E1]" />}
              <div className="min-w-0 flex-1">
                <div className={`font-heading text-base font-bold ${complete ? "text-[#94A3B8] line-through" : "text-[#1F2937]"}`}>{s.title}</div>
                {!complete && <div className="font-body text-sm text-[#6B7280]">{s.desc}</div>}
              </div>
              {!complete && (
                <button onClick={s.onClick} data-testid={`onboarding-cta-${i}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#0D9488] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#0B7F74]">
                  {s.cta} <ArrowRight size={15} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
